import { FC, useState, useEffect } from 'react';
import { AnyAction } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import {
  AlertMessage,
  Breakpoints,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonTypes,
  Drawer,
  FAIcon,
  IconPropInterface,
  IconStyle,
  Theme,
  useBreakpoint,
  useSession,
  useTranslation,
} from '@nextgen-web-framework/all';
import { StorageService } from '@nextgen-web-framework/core';
import { ABOCheckboxCard } from '@commerce-product-webcomponents/ui';
import {
  IProduct,
  DerivedAvailabilityStatus,
  productDataTransformer,
  productApi,
  Products,
  ProductData,
} from '@commerce-product-webcomponents/sdk';
import { CheckoutProfile } from '@commerce-checkout-webcomponents/sdk';
import { updateMyShopSliceReq } from '@commerce-myshop-webcomponents/sdk';
import type { FeatureProductButtonProps } from './types/propTypes';
import styles from './FeatureProductButton.module.scss';

const FeatureProductButton: FC<FeatureProductButtonProps> = (props: FeatureProductButtonProps): JSX.Element => {
  const t = useTranslation();
  const viewPortType = useBreakpoint();
  const dispatch = useDispatch();
  const { maxAllowableSelection, availableProductIds, selectedProducts, version } = props;
  const [drawer, setDrawer] = useState<boolean>(false);
  const [selectedProductList, setSelectedProductList] = useState<IProduct[]>([]);
  const [preSelectedProductList, setPreSelectedProductList] = useState<IProduct[]>([]);
  const { title, icon, drawerPrimaryButtonText, drawerPrimaryButtonDisable, drawerAlertMessage } =
    getSupplementaryProps(
      selectedProducts !== undefined && selectedProducts.length > 0,
      selectedProductList,
      maxAllowableSelection,
      t,
    );
  const excludedStatuses = [
    DerivedAvailabilityStatus.OutOfStock,
    DerivedAvailabilityStatus.Nla,
    DerivedAvailabilityStatus.Nya,
  ];
  const [availableProducts, setAvailableProducts] = useState<IProduct[]>([]);
  const isEdit = selectedProducts && selectedProducts.length > 0;
  const localStorage = StorageService.getLocalStorage();
  const { session } = useSession();

  useEffect(() => {
    getProducts(availableProductIds)
      .then((products) => {
        setAvailableProducts(products);
      })
      .catch(() => {
        setAvailableProducts([]);
      });
  }, [availableProductIds && availableProductIds.length > 0]);

  useEffect(() => {
    if (selectedProducts && availableProducts) {
      setSelectedProductList(
        availableProducts.filter(
          (p: IProduct) =>
            selectedProducts.includes(p.productId) && !excludedStatuses.some((s) => s === p.derivedAvailabilityStatus),
        ),
      );
      setPreSelectedProductList(
        availableProducts.filter(
          (p: IProduct) =>
            selectedProducts.includes(p.productId) && !excludedStatuses.some((s) => s === p.derivedAvailabilityStatus),
        ),
      );
    }
  }, [availableProducts && availableProducts.length > 0]);

  const getProducts = async (ids: string[]) => {
    const checkoutProfile = localStorage.retrieve('checkoutProfile') as unknown as CheckoutProfile;
    const productList: Products = await productApi.getProductByIds({
      itemNumberList: ids,
      projections: 'DETAILED',
      fulfillmentType: checkoutProfile?.fulfillmentType?.id,
      warehouseId: session?.warehouseId,
    });
    const products: IProduct[] | undefined = productList?.products?.map((product: ProductData) =>
      productDataTransformer(product),
    );
    products?.sort((prev, next) => ids.indexOf(prev.productId) - ids.indexOf(next.productId));
    return products ?? [];
  };

  const closeDrawer = () => {
    setDrawer(false);
    setSelectedProductList(preSelectedProductList);
  };

  const onABOCheckboxClick = (prodId: string) => {
    const p = availableProducts.find((p: IProduct) => p.productId == prodId);
    const inProductList = selectedProductList.find((p) => p.productId == prodId);
    if (p) {
      if (inProductList) {
        setSelectedProductList(selectedProductList.filter((p) => p.productId !== prodId));
      } else {
        setSelectedProductList(selectedProductList.concat([p]));
      }
    }
    return prodId;
  };

  const setABOCheckboxCheckedState = (prodId: string): boolean =>
    selectedProductList.find(
      (p: IProduct) => p.productId == prodId && !excludedStatuses.some((s) => s === p.derivedAvailabilityStatus),
    ) !== undefined;

  const setABOCheckboxDisableState = (prodId: string): boolean => {
    if (selectedProductList.length >= maxAllowableSelection) {
      return !setABOCheckboxCheckedState(prodId);
    }
    if (
      availableProducts.find(
        (p: IProduct) => p.productId == prodId && excludedStatuses.some((s) => s === p.derivedAvailabilityStatus),
      )
    ) {
      return true;
    }
    return false;
  };

  const onUpdateFeatureProducts = () => {
    dispatch(
      updateMyShopSliceReq({
        payload: {
          featureProducts: selectedProductList.map((prod: IProduct) => prod.productId),
          version: version,
        } as unknown as BodyInit,
      }) as unknown as AnyAction,
    );
    setDrawer(false);
  };

  return (
    <>
      <div
        className={`${styles['fp-text-link-wrapper']} ${!isEdit ? styles['feature-product-button-add'] : ''}`}
        onKeyDown={(e) => {
          e.preventDefault();
          setDrawer(true);
        }}
        onClick={(e) => {
          e.preventDefault();
          setDrawer(true);
        }}
        role="link"
        tabIndex={0}
        aria-label="text-link"
        data-testid="textlink-test-id"
      >
        <span className={styles['fp-text-link-wrapper-icon']}>
          <FAIcon iconStyle={icon.iconStyle} theme={Theme.PURPLE} iconName={icon.iconName} />
          <span className={styles['fp-text-link-wrapper-icon-text']}></span>
        </span>
        <span className={styles['fp-text-link-wrapper-text']}>{title}</span>
      </div>
      {drawer && (
        <div data-testid="feature-product-drawer-testid" className={styles['feature-product-drawer']}>
          <Drawer
            bottomGap={0.5}
            footer={
              <div className={styles['footer-drawer']}>
                <div className={styles['cancel-btn']}>
                  <Button
                    data-testid="cancel-testid"
                    buttonStyle={ButtonStyles.OUTLINE}
                    size={
                      viewPortType != Breakpoints.XS && viewPortType != Breakpoints.SM
                        ? ButtonSize.MEDIUM
                        : ButtonSize.SMALL
                    }
                    type={ButtonTypes.BUTTON}
                    title={t('myshop.FeatureProduct.cancel')}
                    onClick={() => closeDrawer()}
                  />
                </div>
                <div className={styles['footer-drawer-section']}>
                  <Button
                    data-testid="save-testid"
                    title={drawerPrimaryButtonText}
                    size={
                      viewPortType != Breakpoints.XS && viewPortType != Breakpoints.SM
                        ? ButtonSize.MEDIUM
                        : ButtonSize.SMALL
                    }
                    buttonStyle={ButtonStyles.SOLID}
                    type={ButtonTypes.BUTTON}
                    disabled={drawerPrimaryButtonDisable}
                    onClick={() => {
                      onUpdateFeatureProducts();
                    }}
                  />
                </div>
              </div>
            }
            header={<div>{title}</div>}
            subHeader={
              drawerAlertMessage !== undefined && (
                <AlertMessage theme={Theme.WARNING} className={styles['feature-product-drawer-alert']}>
                  {drawerAlertMessage}
                </AlertMessage>
              )
            }
            closeIcon={true}
            onClose={closeDrawer}
          >
            {availableProducts.map((p: IProduct) => (
              <ABOCheckboxCard
                key={p.productId}
                productData={
                  {
                    alt: p.alt,
                    mainLink: p.mainLink,
                    productId: p.productId,
                    brandName: p.brandName,
                    productName: p.productName,
                    productSize: p.productSize,
                    productPrice: p.productPrice,
                    currency: p.currency,
                    isBundle: p.isBundle,
                    minPrice: p.minPrice || 0,
                    maxPrice: p.maxPrice || 0,
                    derivedAvailabilityStatus: p.derivedAvailabilityStatus,
                    pointValue: p.pointValue,
                    businessVolume: p.businessVolume,
                  } as unknown as IProduct
                }
                searchedKeyword={''}
                onABOCheckboxClick={onABOCheckboxClick}
                initialCheckBoxVal={setABOCheckboxCheckedState(p.productId)}
                isCheckBoxDisabled={setABOCheckboxDisableState(p.productId)}
                entryObj={{
                  productId: '',
                  entryId: 1,
                }}
                className={styles['abo-checkbox-content']}
              />
            ))}
          </Drawer>
        </div>
      )}
    </>
  );
};

export default FeatureProductButton;

export const getDrawerAlertMessage = (selectedProductList: IProduct[], maxAllowableSelection: number, t: Function) => {
  if (selectedProductList.length === 0) {
    return t('myshop.FeatureProduct.noProductSelected');
  }
  if (selectedProductList.length >= maxAllowableSelection) {
    return t('myshop.FeatureProduct.maximumSelectionAllowed').replace('{0}', maxAllowableSelection);
  }
  return undefined;
};

const getSupplementaryProps = (
  isEdit: boolean,
  selectedProductList: IProduct[],
  maxAllowableSelection: number,
  t: Function,
): {
  title: string;
  icon: IconPropInterface;
  drawerPrimaryButtonText: string;
  drawerPrimaryButtonDisable: boolean;
  drawerAlertMessage: undefined | string;
} => {
  if (isEdit) {
    return {
      title: t('myshop.FeatureProduct.editProducts'),
      icon: {
        iconName: 'pen',
        iconStyle: IconStyle.REGULAR,
      },
      drawerPrimaryButtonText: t('myshop.FeatureProduct.save'),
      drawerPrimaryButtonDisable: false,
      drawerAlertMessage: getDrawerAlertMessage(selectedProductList, maxAllowableSelection, t),
    };
  }
  return {
    title: t('myshop.FeatureProduct.addProducts'),
    icon: {
      iconName: 'plus',
      iconStyle: IconStyle.SOLID,
    },
    drawerPrimaryButtonText: t('myshop.FeatureProduct.addProducts'),
    drawerPrimaryButtonDisable: true && selectedProductList.length === 0,
    drawerAlertMessage:
      selectedProductList.length >= maxAllowableSelection
        ? t('myshop.FeatureProduct.maximumSelectionAllowed').replace('{0}', maxAllowableSelection)
        : undefined,
  };
};
